import { PropsWithChildren } from "react";

import NextImage from "next/legacy/image";

import { Link } from "@chakra-ui/next-js";
import {
  Box,
  Center,
  Container,
  Flex,
  Grid,
  GridItem,
  Heading,
  SimpleGrid,
  Spacer,
  Text,
  useColorModeValue,
  VStack,
} from "@chakra-ui/react";

import { Product } from "@/__generated__/graphql";
import Logo from "@/components/shared/Logo";
import MarketingBox from "@/components/shared/MarketingBox";
import { BugIcon, ChartIcon, PingPongIcon } from "@/components/shared/ReleaseIcons";

const signUp = (
  <>
    Don&apos;t have an account?{" "}
    <Link fontWeight="bold" href="/register">
      Sign Up
    </Link>
  </>
);

function InstantDatasetsLoginLayout({
  children,
  secondaryAction = signUp,
  hideRightPanel = false,
}: PropsWithChildren<{ secondaryAction?: JSX.Element; hideRightPanel?: boolean }>) {
  const color = useColorModeValue("plum.700", "gray.300");

  return (
    <SimpleGrid columns={[1, 1, 1, 1, hideRightPanel ? 1 : 2]} height="100vh" spacing={0}>
      <Grid gridTemplateColumns={"1fr"} gridTemplateRows={"auto 1fr auto"} p={10}>
        <Flex flexDirection={{ base: "column", sm: "row" }}>
          <Logo h="33px" product={Product.StandaloneDatasets} />
          <Spacer />
          <Text display={{ base: "block", xl: hideRightPanel ? "block" : "none" }} textAlign="center">
            {secondaryAction}
          </Text>
        </Flex>
        <Flex alignItems="center" display="flex" justifyContent="center">
          {children}
        </Flex>
        <Container maxW="md" textAlign="center">
          Logging in signifies that you have read and agree to the{" "}
          <Link
            fontWeight="bold"
            href="https://release.com/terms-of-service"
            rel="noopener noreferrer"
            target="_blank"
          >
            Terms of Service
          </Link>{" "}
          and our{" "}
          <Link
            fontWeight="bold"
            href="https://release.com/privacy-policy"
            rel="noopener noreferrer"
            target="_blank"
          >
            Privacy Policy
          </Link>
          .
        </Container>
      </Grid>
      {!hideRightPanel && (
        <Grid
          bg="background.darker"
          display={{ base: "none", xl: "grid" }}
          gridTemplateColumns={"1fr"}
          gridTemplateRows={"auto 1fr"}
          p={10}
        >
          <Text textAlign="end">{secondaryAction}</Text>
          <Center flexDirection="column">
            <Container maxW="2xl">
              <VStack alignItems="start" spacing={10}>
                <Heading color={color} lineHeight="44px" size="lg" whiteSpace="nowrap">
                  Instant Datasets
                  <br />
                  Build and test with production-like data
                </Heading>
                <Grid columnGap={6} gridTemplateColumns={"1fr 1fr 1fr"}>
                  <GridItem>
                    <MarketingBox
                      color={color}
                      description="[real data = real bugs]"
                      heading="Eliminate bugs before they reach production"
                      icon={BugIcon}
                    />
                  </GridItem>
                  <GridItem>
                    <MarketingBox
                      color={color}
                      description="[real data = real loads]"
                      heading="See actual app behavior under realistic loads"
                      icon={ChartIcon}
                    />
                  </GridItem>
                  <GridItem>
                    <MarketingBox
                      color={color}
                      description="[real data = real results]"
                      heading="Reduce back and forth with business owners"
                      icon={PingPongIcon}
                    />
                  </GridItem>
                </Grid>
                <Box h="428px" position="relative" w="100%">
                  <NextImage
                    priority
                    alt="instant datasets"
                    layout="fill"
                    objectFit="contain"
                    src="/images/instantdatasets.png"
                  />
                </Box>
              </VStack>
            </Container>
          </Center>
        </Grid>
      )}
    </SimpleGrid>
  );
}

export default InstantDatasetsLoginLayout;
